import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Badge, Spinner } from 'react-bootstrap';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import { getEmployees, createEmployee, deleteEmployee } from '../services/firebase/employeeService';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';

const EmployeePage = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'employee',
        department: '',
        designation: '',
        salary: '',
        joiningDate: ''
    });
    const { user } = useAuth();

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            const response = await getEmployees();
            setEmployees(response.data?.employees || []);
        } catch (error) {
            toast.error('Failed to fetch employees');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await createEmployee(formData);
            toast.success('Employee created successfully');
            setShowModal(false);
            fetchEmployees();
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                role: 'employee',
                department: '',
                designation: '',
                salary: '',
                joiningDate: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create employee');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this employee?')) {
            try {
                await deleteEmployee(id);
                toast.success('Employee deleted successfully');
                fetchEmployees();
            } catch (error) {
                toast.error('Failed to delete employee');
            }
        }
    };

    return (
        <div className="d-flex">
            <Sidebar />
            <div className="flex-grow-1" style={{ background: '#f4f6f9', minHeight: '100vh' }}>
                <Navbar />
                <Container fluid className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="fw-bold text-dark">Employee Management</h2>
                        {(user?.role === 'admin' || user?.role === 'hr') && (
                            <Button variant="primary" onClick={() => setShowModal(true)}>
                                <FaPlus className="me-2" /> Add Employee
                            </Button>
                        )}
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <div className="bg-white rounded shadow-sm p-3">
                            <Table hover responsive className="align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Department</th>
                                        <th>Designation</th>
                                        <th>Joining Date</th>
                                        {(user?.role === 'admin') && <th>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {employees.length > 0 ? (
                                        employees.map((emp) => (
                                            <tr key={emp.uid}>
                                                <td className="fw-bold">{emp.firstName} {emp.lastName}</td>
                                                <td>{emp.email}</td>
                                                <td>
                                                    <Badge bg={emp.role === 'admin' ? 'danger' : emp.role === 'hr' ? 'warning' : 'info'}>
                                                        {emp.role.toUpperCase()}
                                                    </Badge>
                                                </td>
                                                <td>{emp.department || '-'}</td>
                                                <td>{emp.designation || '-'}</td>
                                                <td>{emp.joiningDate ? new Date(emp.joiningDate).toLocaleDateString() : '-'}</td>
                                                {(user?.role === 'admin') && (
                                                    <td>
                                                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(emp.uid)}>
                                                            <FaTrash />
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 text-muted">No employees found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Add Employee Modal */}
                    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Add New Employee</Modal.Title>
                        </Modal.Header>
                        <Form onSubmit={handleSubmit}>
                            <Modal.Body>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>First Name</Form.Label>
                                            <Form.Control name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Last Name</Form.Label>
                                            <Form.Control name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Email</Form.Label>
                                            <Form.Control type="email" name="email" value={formData.email} onChange={handleInputChange} required />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Password</Form.Label>
                                            <Form.Control type="password" name="password" value={formData.password} onChange={handleInputChange} required />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Role</Form.Label>
                                            <Form.Select name="role" value={formData.role} onChange={handleInputChange}>
                                                <option value="employee">Employee</option>
                                                <option value="hr">HR</option>
                                                <option value="admin">Admin</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Department</Form.Label>
                                            <Form.Control name="department" value={formData.department} onChange={handleInputChange} />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Designation</Form.Label>
                                            <Form.Control name="designation" value={formData.designation} onChange={handleInputChange} />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label>Joining Date</Form.Label>
                                            <Form.Control type="date" name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} required />
                                        </Form.Group>
                                    </div>
                                    {user?.role === 'admin' && (
                                        <div className="col-md-12">
                                            <Form.Group>
                                                <Form.Label>Salary (CTC)</Form.Label>
                                                <Form.Control type="number" name="salary" value={formData.salary} onChange={handleInputChange} />
                                            </Form.Group>
                                        </div>
                                    )}
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                                <Button variant="primary" type="submit">Create Employee</Button>
                            </Modal.Footer>
                        </Form>
                    </Modal>
                </Container>
            </div>
        </div>
    );
};

export default EmployeePage;
